import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { ANAG_TYPES, displayName } from "../lib/anagConfig";
import useCompagniaMatcher from "../lib/useCompagniaMatcher";
import CompagnieMultiSelect from "./CompagnieMultiSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Pencil, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

function kindOf(atype) {
  const c = ANAG_TYPES.find((t) => t.key === atype);
  return c && c.fields.some((f) => f.key === "denominazione") ? "azienda" : "persona";
}

// Tendina di professionisti filtrata per compagnia + modifica rapida dell'anagrafica.
export default function ProSelect({ atype, value, onChange, compagnia, testId, label }) {
  const [items, setItems] = useState([]);
  const [editRec, setEditRec] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const kind = kindOf(atype);
  const { inCompagnia } = useCompagniaMatcher(!!compagnia);

  const load = async () => {
    try { const { data } = await api.get(`/anagrafiche/${atype}`); setItems(data); } catch (e) { setItems([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [atype]);

  const filtered = compagnia
    ? items.filter((i) => inCompagnia(i.compagnie, compagnia))
    : items;
  const names = filtered.map(displayName);
  const extra = value && !names.includes(value) ? [value] : [];

  const openEdit = () => {
    const rec = items.find((i) => displayName(i) === value);
    if (!rec) { toast.info("Seleziona prima un professionista"); return; }
    setForm({ ...rec });
    setEditRec(rec);
  };

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { id, ...payload } = form;
      const { data } = await api.patch(`/anagrafiche/${atype}/${editRec.id}`, payload);
      toast.success("Professionista aggiornato");
      onChange(displayName(data));
      setEditRec(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="mt-1.5 flex items-center gap-1.5">
        <Select value={value || "__none__"} onValueChange={(v) => onChange(v === "__none__" ? "" : v)}>
          <SelectTrigger data-testid={testId} className="bg-slate-900/60 border-slate-700 flex-1 text-slate-100 [&>svg]:opacity-100 [&>svg]:text-slate-100"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent className="bg-[#121824] border-slate-700 text-slate-100 max-h-64">
            <SelectItem value="__none__">Nessuno</SelectItem>
            {extra.map((n) => <SelectItem key={`x-${n}`} value={n}>{n}</SelectItem>)}
            {filtered.map((r) => { const n = displayName(r); return <SelectItem key={r.id} value={n}>{n}</SelectItem>; })}
          </SelectContent>
        </Select>
        <button type="button" onClick={openEdit} data-testid={`${testId}-edit`} title="Modifica rapida"
          className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md border border-slate-700 bg-slate-800/60 text-slate-100 hover:text-amber-400 hover:border-amber-500/40 hover:bg-slate-700/60 transition-colors">
          <Pencil className="h-4 w-4" />
        </button>
      </div>

      <Dialog open={!!editRec} onOpenChange={(o) => !o && setEditRec(null)}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-lg" data-testid={`${testId}-edit-dialog`}>
          <DialogHeader><DialogTitle className="font-head">Modifica · {label || "Professionista"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            {kind === "azienda" ? (
              <div className="sm:col-span-2">
                <Label className="text-slate-300 text-xs">Denominazione</Label>
                <Input value={form.denominazione || ""} onChange={(e) => upd("denominazione")(e.target.value)} data-testid={`${testId}-edit-denominazione`} className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
              </div>
            ) : (
              <>
                <div><Label className="text-slate-300 text-xs">Cognome</Label><Input value={form.cognome || ""} onChange={(e) => upd("cognome")(e.target.value)} data-testid={`${testId}-edit-cognome`} className="mt-1 h-9 bg-slate-900/60 border-slate-700" /></div>
                <div><Label className="text-slate-300 text-xs">Nome</Label><Input value={form.nome || ""} onChange={(e) => upd("nome")(e.target.value)} data-testid={`${testId}-edit-nome`} className="mt-1 h-9 bg-slate-900/60 border-slate-700" /></div>
              </>
            )}
            <div><Label className="text-slate-300 text-xs">Telefono</Label><Input value={form.telefono || ""} onChange={(e) => upd("telefono")(e.target.value)} className="mt-1 h-9 bg-slate-900/60 border-slate-700" /></div>
            <div><Label className="text-slate-300 text-xs">Email</Label><Input value={form.email || ""} onChange={(e) => upd("email")(e.target.value)} className="mt-1 h-9 bg-slate-900/60 border-slate-700" /></div>
            <div className="sm:col-span-2">
              <Label className="text-slate-300 text-xs">Compagnie associate</Label>
              <CompagnieMultiSelect value={form.compagnie || []} onChange={upd("compagnie")} testId={`${testId}-edit-compagnie`} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} disabled={saving} data-testid={`${testId}-edit-save`} className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Salva</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
